import { ClaimModel, ClaimModelFactory } from "@app/models/claim";
import { formatDateAsUk, formatDateAsIso } from "@app/utils/date";
import { nameof } from "@app/utils/nameof";
import { SearchFilterModel } from "@app/models/search/filter";
import { Omit } from '@app/utils/omit';
import { ISubject, Subject, IObservable } from '@app/utils/observable';
import { SearchRequestModel, SearchResponseModel, SearchFilterRequestModel, serializeSearchFilterModel } from '@app/api/models/data';

export class DataTableComponent {
    private dataTable: DataTables.Api;
    private searchFilter: SearchFilterModel | null = null;
    private readonly detailsButtonText: string;
    private readonly detailsColumnName: string = "details";

    private readonly loadingEventSubject: ISubject<DataTableComponent, any>;

    public get onLoading$(): IObservable<DataTableComponent, any> {
        return this.loadingEventSubject;
    }

    private readonly loadedEventSubject: ISubject<DataTableComponent, DataTableLoadedArgs>;

    public get onLoaded$(): IObservable<DataTableComponent, DataTableLoadedArgs> {
        return this.loadedEventSubject;
    }

    private readonly preXhrEventSubject: ISubject<DataTableComponent, SearchRequestModel>;

    public get onPreXhr$(): IObservable<DataTableComponent, SearchRequestModel> {
        return this.preXhrEventSubject;
    }

    constructor(tableElement: HTMLTableElement) {
        this.loadingEventSubject = new Subject;
        this.loadedEventSubject = new Subject;
        this.preXhrEventSubject = new Subject;

        var languageObject = document.documentElement.lang === 'cy' ?
            {
                "paginate": {
                    "first": "Cyntaf",
                    "last": "Olaf",
                    "next": "Nesaf",
                    "previous": "Blaenorol"
                },
                "info": "Yn dangos _START_ i _END_ o _TOTAL_ cofnod"
            } :
            {};

        this.dataTable = $(tableElement).DataTable({
            columnDefs: [
                {
                    targets: "_all",
                    searchable: false
                },
                {
                    targets: [3],
                    render: {
                        display: (column: any, type: "display" | "sort" | "filter" | "type", row: any, meta: any): any => {
                            return formatDateAsUk(column as Date);
                        }
                    }
                },
                {
                    targets: [4],
                    render: $.fn.dataTable.render.number(",", ".", 2, "£")
                },
                {
                    targets: [5],
                    render: {
                        display: (column: any, type: "display" | "sort" | "filter" | "type", row: any, meta: any): any => {
                            return $("<a></a>")
                                .attr("href", `/Claim/${column}`)
                                .attr("target", "_blank")
                                .addClass("button button--pink")
                                .text(this.detailsButtonText)
                                .get(0)!
                                .outerHTML;
                        }
                    },
                    orderable: false
                }
            ],
            columns: [
                {
                    className: "field--member" + (document.documentElement.lang === "cy" ? "-cy" : ""),
                    data: nameof<ClaimModel>("memberName")
                },
                {
                    className: "field--allowance-type" + (document.documentElement.lang === "cy" ? "-cy" : ""),
                    data: nameof<ClaimModel>("allowanceDescription")
                },
                {
                    className: "field--expenditure-type" + (document.documentElement.lang === "cy" ? "-cy" : ""),
                    data: nameof<ClaimModel>("expenditureDescription")
                },
				{
                    className: "table-data--numeric field--date" + (document.documentElement.lang === "cy" ? "-cy" : ""),
                    data: nameof<ClaimModel>("claimDate")
                },
				{
                    className: "table-data--numeric field--value" + (document.documentElement.lang === "cy" ? "-cy" : ""),
                    data: nameof<ClaimModel>("amount")
                },
                {
                    data: nameof<ClaimModel>("id"),
                    name: this.detailsColumnName
                }
            ],
            searching: false,
            language: languageObject,
            lengthChange: false,
            paging: true,
            serverSide: true,
            deferLoading: 0,
            pageLength: 25,
            dom: `lfrt<"search-results__info"i><"search-results__pagination"p>`,
            ajax: {
                url: "/api/data",
                data: (dataTablesConfig: DataTables.AjaxDataRequest, settings: DataTables.Settings): SearchRequestModel => {
                    this.loadingEventSubject.emit(this, null);

                    return $.extend({}, this.prepareDataTablesConfigForAjax(dataTablesConfig), {
                        searchFilter: serializeSearchFilterModel(this.searchFilter!)
                    });
                },
                dataSrc: (response: SearchResponseModel) => {
                    this.loadedEventSubject.emit(this, { success: true, claimsTotal: response.claimsTotal });
                    
                    return response.data.map(d => ClaimModelFactory.build(d));
                }
            }
        } as DataTables.Settings)
        .on("preXhr.dt", (e, settings, data: SearchRequestModel) => {
            this.preXhrEventSubject.emit(this, data);
        })
        .on("error.dt", (e, settings, techNote, message) => {
            this.loadedEventSubject.emit(this, { success: false, error: message });
        });

        this.detailsButtonText = $(this.dataTable.column(`${this.detailsColumnName}:name`).header()).data("text");
    }

    private prepareDataTablesConfigForAjax(dataTablesConfig:  DataTables.AjaxDataRequest) {
        for (let column of dataTablesConfig.columns as any) {
            delete column.name;
            delete column.search;
            delete column.searchable;
        }

        delete (dataTablesConfig as any).search;

        return dataTablesConfig;
    }

    setSearchFilterModel(model: SearchFilterModel) {
        this.searchFilter = model;
    }

    reload() {
        if (this.searchFilter === null) {
            throw new Error("searchFilter is null");
        }

        this.dataTable.ajax.reload();
    }

   
}

export type DataTableLoadedArgs = DataTableLoadedSuccessArgs | DataTableLoadedFailArgs;

export interface DataTableLoadedFailArgs {
    success: false;
    error: string;
}

export interface DataTableLoadedSuccessArgs {
    success: true;
    claimsTotal: number;
}